import { createTheme, darken, type Shadows } from '@mui/material';

const createShadow = (px: number) => {
  return `0 0 ${px}px 0 rgba(53,64,82,.07)`;
};

const shadows: Shadows = [
  'none',
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
  createShadow(14),
];

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: '#3e52ff',
      light: '#8e9aff',
      contrastText: '#fff',
    },
    secondary: {
      main: '#101224',
      contrastText: '#fff',
    },

    // Figma の Safe Color
    success: {
      main: '#E3FFF6', // fill 色
      light: '#E8F5E9', // alert 背景色
      dark: '#00A063', // 白背景上のテキスト色
      contrastText: '#006338', // fill背景上のテキスト色
    },

    // Figma の Warning Color
    warning: {
      main: '#FFAA00',
      light: '#FFE8C4',
      dark: '#FF002B',
      contrastText: '#5B0014',
    },

    // Figma の Danger Color
    error: {
      main: '#FF4069',
      light: '#FFD8E2',
      dark: '#FF002B',
      contrastText: '#5B0014',
    },

    // Figma の Info Color
    info: {
      main: '#8E9AFF',
      light: '#E4E9FA',
      dark: '#3E52FF',
      contrastText: '#000639',
    },
  },
  typography: {
    fontFamily: ['Noto Sans JP', 'sans-serif'].join(','),
    fontSize: 14,
    body1: {
      fontSize: 14,
    },
  },
  shadows,
  components: {
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          if (ownerState.color === 'info') {
            return {
              color: theme.palette.info.dark,
            };
          } else if (ownerState.color === 'error') {
            return {
              color: theme.palette.error.dark,
            };
          } else if (ownerState.color === 'warning') {
            return {
              color: theme.palette.warning.dark,
            };
          } else if (ownerState.color === 'success') {
            return {
              color: theme.palette.success.dark,
            };
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f4fa',
          color: '#1c1b1f',
          '.MuiIconButton-root': {
            color: '#1c1b1f',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#101224',
          color: '#ffffff',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: 'solid 1px #C6C6C5',
          borderRadius: 8,
          position: 'relative',
          '&::after': {
            content: '""',
            display: 'block',
            height: 16,
            backgroundColor: '#ECECEC',
            borderTop: '1px solid #C6C6C5',
            position: 'sticky',
            bottom: 0,
            left: 0,
            width: '100%',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '.MuiTableCell-body': {
            borderBottom: '1px solid #C6C6C5',
          },
          '.MuiTableRow-root': {
            '&:last-of-type': {
              '.MuiTableCell-body': {
                borderBottom: 'none',
              },
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          th: {
            height: 36,
            backgroundColor: '#ececec',
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const color = ownerState.color;
          const variant = ownerState.variant;
          const commonStyles = {
            borderRadius: 18,
            textTransform: 'none',
            '&.MuiButton-sizeSmall': {
              padding: '5px 20px',
            },
          };

          if (variant === 'contained') {
            if (color === 'error') {
              return {
                ...commonStyles,
                backgroundColor: theme.palette.error.main,
                color: theme.palette.common.white,

                '&:hover': {
                  backgroundColor: darken(theme.palette.error.main, 0.2),
                },
              };
            } else if (color === 'info') {
              return {
                ...commonStyles,
                backgroundColor: theme.palette.info.dark,
                color: theme.palette.common.white,

                '&:hover': {
                  backgroundColor: darken(theme.palette.info.dark, 0.2),
                },
              };
            } else {
              return {
                ...commonStyles,
                backgroundColor: theme.palette.info.dark,
                color: theme.palette.common.white,

                '&:hover': {
                  backgroundColor: darken(theme.palette.info.dark, 0.2),
                },
              };
            }
          }

          if (variant === 'outlined') {
            return {
              ...commonStyles,
              backgroundColor: theme.palette.common.white,
              color: theme.palette.common.black,
              borderColor: theme.palette.common.black,
              opacity: 0.87,
            };
          }

          return commonStyles;
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: '#C9D4F5',
          fontSize: 16,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '.MuiDialogTitle-root + &': {
            paddingTop: 24,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 24,
          paddingTop: 0,
          '>:not(style)~:not(style)': {
            marginLeft: 24,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 28,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-notchedOutline': {
            borderRadius: 4,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-notchedOutline': {
            borderRadius: 4,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-outlined': {
            '&.MuiChip-colorSuccess': {
              borderColor: '#00BA77',
              color: '#00BA77',
              backgroundColor: '#E6FFFA',
            },
            '&.MuiChip-colorError': {
              borderColor: '#FF4D4F',
              color: '#ff4d4f',
              backgroundColor: '#FFF1F0',
            },
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#8a99c7',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.3)',
          width: '477px',
          variants: [
            {
              props: { variant: 'outlined', severity: 'info' },
              style: {
                backgroundColor: '#E4E9FA',
                borderColor: '#3E52FF',
                color: '#000639',
                '& .MuiAlert-icon': {
                  color: '#3E52FF',
                },
              },
            },
            {
              props: { variant: 'outlined', severity: 'warning' },
              style: {
                color: '#512F03',
                backgroundColor: '#FFE8C4',
                borderColor: '#D37800',
                '& .MuiAlert-icon': {
                  color: '#D37800',
                },
              },
            },
            {
              props: { variant: 'outlined', severity: 'error' },
              style: {
                backgroundColor: '#FFD8E2',
                borderColor: '#FF002B',
                color: '#5B0014',
                '& .MuiAlert-icon': {
                  color: '#FF002B',
                },
              },
            },
          ],
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '.notistack-MuiContent-success': {
          backgroundColor: '#E3FFF6',
          color: '#006338',
          '#notistack-snackbar': {
            svg: {
              color: '#00BA77',
            },
          },
          '.MuiSvgIcon-root': {
            color: '#006338',
          },
        },
        '.notistack-MuiContent-info': {
          backgroundColor: '#E4E9FA',
          color: '#000639',
          '#notistack-snackbar': {
            svg: {
              color: '#3E52FF',
            },
          },
          '.MuiSvgIcon-root': {
            color: '#000639',
          },
        },
        '.notistack-MuiContent-warning': {
          color: '#512F03',
          backgroundColor: '#FFE8C4',
          '#notistack-snackbar': {
            svg: {
              color: '#D37800',
            },
          },
          '.MuiSvgIcon-root': {
            color: '#512F03',
          },
        },
        '.notistack-MuiContent-error': {
          backgroundColor: '#FFD8E2',
          color: '#5B0014',
          '#notistack-snackbar': {
            svg: {
              color: '#FF002B',
            },
          },
          '.MuiSvgIcon-root': {
            color: '#5B0014',
          },
        },
      },
    },
  },
});

export default theme;
